import { Button, Card, CardContent, Dialog, Grid } from '@mui/material';
import { useFieldArray } from 'react-hook-form';
import { BuildingForm } from './building.form';
import { CreateBuildingButton } from './create-building.button';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { Project, ProjectType } from '../../../models/project';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { projectBuildingsValidationSchema } from './building-validation.schema';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useState } from 'react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../../hooks/use-authorize';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';

interface BuildingUnitsListFormProps {
  projectID: number;
  projectBuildings: any[];
  page?: string;
  refetch: () => void;
  syncWithDux: any;
  projectTypeValue: any;
  projectStreet?: string;
  projectCommunityType?: string;
}

export const BuildingUnitsListForm = ({
  projectID,
  projectBuildings,
  page,
  syncWithDux,
  projectTypeValue,
  refetch,
  projectStreet,
  projectCommunityType,
}: BuildingUnitsListFormProps) => {
  const updatedProjectBuildings =
    projectTypeValue === 'RES'
      ? projectBuildings
      : projectBuildings?.map((building) => ({
          ...building,
          buildingFloors: building.buildingFloors?.map((floor: any) => ({
            ...floor,
            buildingLots: floor.buildingLots?.map((lot: any) => ({
              ...lot,
            })),
          })),
        }));

  const form = useReactHookForm({
    defaultValues: {
      projectID,
      projectBuildings: updatedProjectBuildings,
    },
    yupSchema: projectBuildingsValidationSchema,
  });

  const { fields, prepend, remove } = useFieldArray({
    control: form.control,
    name: 'projectBuildings',
    keyName: 'bID',
  });
  const { showSnackbar } = useSnackbar();
  const { isLoading, mutateAsync } = useSaveData(projectsApiKeyStore.updateLotInfo());
  const [transformedData, setTransformedData] = useState<any>({});
  const [openDialog, setOpenDialog] = useState(false);

  const handleNewBudingCreated = (data: Project) => {
    prepend(data.projectBuildings);
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  const transformProctorResultForCommonArea = (area: any, proctorResult: any) => {
    return {
      proctorResultsID: proctorResult.proctorResultsID,
      unitID: !area?.commonAreaID ? 0 : area?.commonAreaID,
      proctorFileName: proctorResult.proctorFileName,
    };
  };

  const transformProctorResult = (lot: any, proctorResult: any) => {
    // return proctorResult?.map((proctor: any) => ({
    //   proctorResultsID: proctor.proctorResultsID,
    //   unitID: !lot?.unitID ? 0 : lot?.unitID,
    //   proctorFileName: proctor.proctorFileName,
    // }));

    return {
      proctorResultsID: proctorResult.proctorResultsID,
      unitID: !lot?.unitID ? 0 : lot?.unitID,
      proctorFileName: proctorResult.proctorFileName,
    };
  };

  const transformBuildingLots = (buildingLots: any) => {
    return buildingLots?.map((lot: any) => ({
      ...lot,
      squareFeet: !lot?.squareFeet ? null : Number(lot?.squareFeet),
      proctorResult: lot.proctorResult ? transformProctorResult(lot, lot.proctorResult) : null,
    }));
  };

  const transformBuildingFloors = (buildingFloors: any) => {
    return buildingFloors?.map((floor: any) => ({
      ...floor,
      buildingLots: floor.buildingLots ? transformBuildingLots(floor.buildingLots) : [],
    }));
  };

  const transformBuildingCommonAreas = (commonAreas: any) => {
    return commonAreas?.map((area: any) => ({
      ...area,
      proctorResult: area.proctorResult ? transformProctorResultForCommonArea(area, area.proctorResult) : null,
    }));
  };

  const transformProjectBuildings = (projectBuildings: any) => {
    return projectBuildings?.map((building: any) => ({
      ...building,
      buildingFloors: building.buildingFloors ? transformBuildingFloors(building.buildingFloors) : [],
      buildingCommonAreas: building.buildingCommonAreas
        ? transformBuildingCommonAreas(building.buildingCommonAreas)
        : [],
    }));
  };

  const handleSubmit = async (values: any) => {
    const newData = {
      ...values,
      projectBuildings: values.projectBuildings ? transformProjectBuildings(values.projectBuildings) : [],
    };
    const { type, ...dataWithoutType } = newData;

    const hasCreateWOTrue = newData?.projectBuildings?.some((building: any) =>
      building?.buildingFloors?.some((floor: any) => floor?.buildingLots?.some((lot: any) => lot.createWO === true))
    );
    if (!hasCreateWOTrue && type == 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return;
    }

    if (hasCreateWOTrue && type == 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return;
    }

    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: type == 'with-workorder' } }).then(
      (data: any) => {
        form.setValue('projectBuildings', data?.data?.projectBuildings);
        refetch();
      }
    );
    // setTransformedData(newData);
    // setOpenDialog(true);
    // await mutateAsync({ data: newData });
  };

  const closeDialog = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: false } }).then((data: any) => {
      form.setValue('projectBuildings', data?.data?.projectBuildings);
    });
    setOpenDialog(false);
    refetch();
  };

  const handleSubmitConfirm = async () => {
    const { type, ...dataWithoutType } = transformedData;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: true } }).then((data: any) => {
      form.setValue('projectBuildings', data?.data?.projectBuildings);
    });

    setOpenDialog(false);
    refetch();
  };

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  const getAllBuildingNamesWithPaths = (): { name: string; path: string }[] => {
    const projectBuildings = form.getValues('projectBuildings') ?? [];
    return projectBuildings.map((building: any, idx: number) => ({
      name: normalize(building?.buildingName ?? ''),
      path: `projectBuildings.${idx}.buildingName`,
    }));
  };

  const getAllUnitNamesWithPaths = () => {
    const projectBuildings = form.getValues('projectBuildings') ?? [];
    const result: { name: string; path: string }[] = [];

    projectBuildings.forEach((building: any, buildingIdx: number) => {
      (building?.buildingFloors ?? []).filter(Boolean).forEach((floor: any, floorIdx: number) => {
        (floor?.buildingLots ?? []).filter(Boolean).forEach((lot: any, lotIdx: number) => {
          if (lot?.unitName) {
            result.push({
              name: normalize(lot.unitName),
              path: `projectBuildings.${buildingIdx}.buildingFloors.${floorIdx}.buildingLots.${lotIdx}.unitName`,
            });
          }
        });
      });
      (building?.buildingCommonAreas ?? []).filter(Boolean).forEach((area: any, areaIdx: number) => {
        if (area?.commonAreaName) {
          result.push({
            name: normalize(area.commonAreaName),
            path: `projectBuildings.${buildingIdx}.buildingCommonAreas.${areaIdx}.commonAreaName`,
          });
        }
      });
    });

    return result;
  };

  const getAllAddressesWithPaths = (): { address: string; path: string }[] => {
    const projectBuildings = form.getValues('projectBuildings') ?? [];
    const result: { address: string; path: string }[] = [];

    projectBuildings.forEach((building: any, buildingIdx: number) => {
      (building?.buildingFloors ?? []).filter(Boolean).forEach((floor: any, floorIdx: number) => {
        (floor?.buildingLots ?? []).filter(Boolean).forEach((lot: any, lotIdx: number) => {
          if (lot?.address) {
            result.push({
              address: normalize(lot.address),
              path: `projectBuildings.${buildingIdx}.buildingFloors.${floorIdx}.buildingLots.${lotIdx}.address`,
            });
          }
        });
      });
      (building?.buildingCommonAreas ?? []).filter(Boolean).forEach((area: any, areaIdx: number) => {
        if (area?.address) {
          result.push({
            address: normalize(area.address),
            path: `projectBuildings.${buildingIdx}.buildingCommonAreas.${areaIdx}.address`,
          });
        }
      });
    });

    return result;
  };

  const clearAllAddressErrors = () => {
    const projectBuildings = form.getValues('projectBuildings') ?? [];
    projectBuildings.forEach((building: any, buildingIdx: number) => {
      (building?.buildingFloors ?? []).forEach((_: any, floorIdx: number) => {
        const lots = building.buildingFloors[floorIdx]?.buildingLots ?? [];
        lots.forEach((_lot: any, lotIdx: number) => {
          form.clearErrors(`projectBuildings.${buildingIdx}.buildingFloors.${floorIdx}.buildingLots.${lotIdx}.address`);
        });
      });
      (building?.buildingCommonAreas ?? []).forEach((_: any, areaIdx: number) => {
        form.clearErrors(`projectBuildings.${buildingIdx}.buildingCommonAreas.${areaIdx}.address`);
      });
    });
  };

  const onTypedSubmit = async (type: string) => {
    let hasBlockingErrors = false;

    // Clear previous building-name duplicate errors so stale ones don't persist
    const projectBuildings = form.getValues('projectBuildings') ?? [];
    projectBuildings.forEach((_: any, idx: number) => {
      form.clearErrors(`projectBuildings.${idx}.buildingName` as any);
    });

    // 1. Block on duplicate building names + set inline field errors
    const buildingNames = getAllBuildingNamesWithPaths();
    const buildingNameValues = buildingNames.map((b) => b.name);
    const dupBuildingNames = buildingNames.filter(
      (b) => b.name && buildingNameValues.filter((n) => n === b.name).length > 1
    );

    if (dupBuildingNames.length > 0) {
      dupBuildingNames.forEach((b) => {
        form.setError(b.path as any, { type: 'manual', message: 'Building name already exists.' });
      });
      showSnackbar(
        'Duplicate building names found. Please ensure all building names are unique before saving.',
        'error'
      );
      hasBlockingErrors = true;
    }

    // 2. Block on duplicate Lot/Unit names + set inline field errors
    const unitNamesWithPaths = getAllUnitNamesWithPaths();
    const unitNameValues = unitNamesWithPaths.map((u) => u.name);
    const dupUnitNames = unitNamesWithPaths.filter(
      (u) => u.name && unitNameValues.filter((n) => n === u.name).length > 1
    );

    if (dupUnitNames.length > 0) {
      dupUnitNames.forEach((u) => {
        form.setError(u.path as any, { type: 'manual', message: 'Lot/Unit name already exists.' });
      });
      showSnackbar(
        'Duplicate Lot/Unit names found. Please ensure all Lot/Unit names are unique before saving.',
        'error'
      );
      hasBlockingErrors = true;
    }

    // 3. Block on duplicate addresses + set inline field errors
    const addressesWithPaths = getAllAddressesWithPaths();
    const addressValues = addressesWithPaths.map((a) => a.address);
    const dupAddresses = addressesWithPaths.filter(
      (a) => a.address && addressValues.filter((v) => v === a.address).length > 1
    );

    if (dupAddresses.length > 0) {
      dupAddresses.forEach((a) => {
        form.setError(a.path as any, { type: 'manual', message: 'Address already exists.' });
      });
      if (projectTypeValue && projectTypeValue !== 'COMRL' && projectCommunityType !== ProjectType.Buildings) {
        showSnackbar('Duplicate addresses found. Please ensure all addresses are unique before saving.', 'error');
        hasBlockingErrors = true;
      }
    }

    if (hasBlockingErrors) return;

    // 4. Clear abbreviation warning errors (informational only, never block submit)
    clearAllAddressErrors();

    form.handleSubmit((values) => handleSubmit({ ...values, type }), handleError)();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  const [allBuildingLots, setAllBuildingLots] = useState<any>();

  const handleSetBuildingLots = (value: any) => {
    setAllBuildingLots(value);
  };

  return (
    <div>
      <ReactHookForm
        formName="PROJECT_BUILDING_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading}
      >
        <Grid container spacing={2}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <CreateBuildingButton
              onBuildingCreated={handleNewBudingCreated}
              projectID={projectID}
              syncWithDux={syncWithDux}
              projectTypeValue={projectTypeValue}
              existingData={allBuildingLots}
            />
          </Grid>
        </Grid>
        <Card elevation={0}>
          <CardContent className="  buildingFormWrap">
            {fields.map((field, index) => (
              <BuildingForm
                page={page}
                key={field.bID}
                name={`projectBuildings.${index}`}
                index={index}
                onRemove={handleRemove}
                syncWithDux={syncWithDux}
                projectTypeValue={projectTypeValue}
                setBuildingLots={handleSetBuildingLots}
                refetch={refetch}
                projectStreet={projectStreet}
                projectCommunityType={projectCommunityType}
              />
            ))}
          </CardContent>
        </Card>
        {fields.length > 0 && (
          <Grid container>
            <Grid item lg={12} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
              <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
                <Button
                  type="button"
                  variant="contained"
                  sx={{ ms: 'auto' }}
                  style={{ backgroundColor: '#e67e22' }}
                  disabled={isLoading || isReadOnlyPermission}
                  onClick={() => onTypedSubmit('without-workorder')}
                >
                  SAVE BUILDINGS WITHOUT WORK ORDER
                </Button>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
                <Button
                  style={{ marginLeft: 10, backgroundColor: '#239b56' }}
                  type="button"
                  variant="contained"
                  sx={{ ms: 'auto' }}
                  disabled={isLoading || isReadOnlyPermission}
                  onClick={() => onTypedSubmit('with-workorder')}
                >
                  SAVE BUILDINGS WITH WORKORDER
                </Button>
              </GuardedComponent>
            </Grid>
          </Grid>
        )}
        {openDialog && (
          <Dialog
            open={openDialog}
            className="modalpopup"
            title={'CONFIRM STATUS'}
            // onClose={closeDialog}
          >
            <div>
              <div style={{ padding: '25px 50px', textAlign: 'center' }}>
                <div>Are you sure you want to generate work orders for all the newly added Lot/Units?</div>
                {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
              </div>
            </div>
            <DialogActionsBar>
              {isReadOnlyPermission && (
                <Button
                  style={{ maxWidth: '140px' }}
                  size="small"
                  type="button"
                  variant="outlined"
                  onClick={() => setOpenDialog(false)}
                >
                  Cancel
                </Button>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
                <button
                  className="btn btn-sm btn-primary btn-lightGrey"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    cursor: 'pointer',
                    padding: 2,
                    marginRight: 6,
                  }}
                  onClick={closeDialog}
                  disabled={isReadOnlyPermission}
                  autoFocus
                >
                  No
                </button>

                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                    color: isReadOnlyPermission ? 'black' : '#fff',
                    cursor: 'pointer',
                    padding: 2,
                  }}
                  type="button"
                  onClick={handleSubmitConfirm}
                  disabled={isLoading || isReadOnlyPermission}
                >
                  Yes
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )}
      </ReactHookForm>
    </div>
  );
};
